import { MediaType, ExternalAssetSource } from "../../../types";
import { BaseAsset } from "../../base";
import { PolyhavenAsset, PolyhavenFilesResponse, PolyhavenFileEntry } from "../types";
import { getBlobDataFromUrl } from "../../../utils";
import { convertGltfToGlb } from '../../../utils/gltf';

export class BasePolyhavenAsset extends BaseAsset {
  protected asset: PolyhavenAsset;
  protected API_BASE_URL: string;

  constructor(file: PolyhavenAsset, apiBaseUrl: string) {
    super();
    this.asset = file;
    this.API_BASE_URL = apiBaseUrl;
  }

  getId(): string {
    return this.asset.id;
  }

  getName(): string {
    return this.asset.name;
  }

  setName(_name: string): void {
    // PolyHaven assets are read-only, so this is a no-op
    // In a real implementation, you might throw an error or log a warning
    console.warn('PolyHaven assets cannot be renamed');
  }

  getMediaType() {
    // Map PolyHaven type numbers to strings
    const typeMap: Record<number, MediaType> = {
      0: MediaType.HDRI,
      1: MediaType.Texture,
      2: MediaType.Model3D,
    };
    return typeMap[this.asset.type] || MediaType.Unknown;
  }

  getImageUrlForCard(size?: { width: number; height?: number }): string {
    if (size) {
      return `${this.asset.thumbnail_url}?w=${size.width}&h=${size.height}`;
    }
    return this.asset.thumbnail_url;
  }

  getPreviewUrl(size?: { width: number; height?: number }): string {
    return this.getImageUrlForCard(size);
  }

  private getDownloadOptions() {
    const option = { entryFormat: '', format: '', resolution: '', convertedFormat: '' };
    switch (this.getMediaType()) {
      case MediaType.HDRI:
        option.entryFormat = 'hdri';
        option.format = 'hdr';
        option.resolution = '4k';
        option.convertedFormat = 'hdr';
        break;
      case MediaType.Texture:
        option.entryFormat = 'gltf';
        option.format = 'gltf';
        option.resolution = '1k';
        option.convertedFormat = 'glb';
        break;
      case MediaType.Model3D:
        option.entryFormat = 'gltf';
        option.format = 'gltf';
        option.resolution = '1k';
        option.convertedFormat = 'glb';
        break;
    }
    return option;
  }

  getFilenameWithExtension(): string {
    const { format, convertedFormat } = this.getDownloadOptions();
    const ext = format === 'gltf' ? convertedFormat : format;
    return `${this.getName()}.${ext}`;
  }

  // Revoke if it's an object URL (i.e. from gltf→glb conversion) After call this method
  // if (downloadUrl.startsWith("blob:")) {
  //   URL.revokeObjectURL(downloadUrl);
  // }
  async getDownloadUrl(): Promise<string> {
    const downloadOption = this.getDownloadOptions();
    const { entryFormat, format, resolution } = downloadOption;

    const filesRes = await fetch(`${this.API_BASE_URL}/files/${this.asset.id}`);
    if (!filesRes.ok) {
      throw new Error(`Failed to fetch download URL for asset ${this.asset.id}`);
    }
    const files: PolyhavenFilesResponse = await filesRes.json();
    const resEntry = files[entryFormat][resolution][format];
    if (!resEntry) {
      throw new Error(
        `No entry for "${this.asset.id}" at ${format}/${resolution}. ` +
        `Available formats: ${Object.keys(files).join(", ")}`
      );
    }

    // No included files — return the URL directly
    if (!resEntry.include || Object.keys(resEntry.include).length === 0) {
      return resEntry.url;
    }

    // If there are included files, we need to fetch them all and convert to glb
    const glbUrl = await this.getGlbFromGltfEntry(resEntry);
    return glbUrl;
  }

  private async getGlbFromGltfEntry(gltfEntry: PolyhavenFileEntry): Promise<string> {
    // Fetch the gltf + all its dependencies in parallel
    const [gltfRes, ...depResponses] = await Promise.all([
      fetch(gltfEntry.url),
      ...Object.values(gltfEntry.include ?? {}).map(dep => fetch(dep.url)),
    ]);

    if (!gltfRes.ok) throw new Error(`Failed to fetch gltf: ${gltfRes.status}`);

    const gltfFilename = gltfEntry.url.split("/").pop()!;
    const gltfFile = new File([await gltfRes.blob()], gltfFilename, { type: "model/gltf+json" });

    const depEntries = Object.entries(gltfEntry.include ?? {});
    const resourceFiles = await Promise.all(
      depResponses.map(async (res, i) => {
        if (!res.ok) throw new Error(`Failed to fetch dependency: ${res.status}`);
        const depFilename = depEntries[i][0].split("/").pop()!;
        return new File([await res.blob()], depFilename);
      })
    );

    // Convert to glb and return an object URL
    const glbFile = await convertGltfToGlb(gltfFile, resourceFiles);
    return URL.createObjectURL(glbFile);
  }

  async downloadFile(): Promise<{ blob: Blob, filename: string }> {
    const downloadUrl = await this.getDownloadUrl();
    const { blob } = await getBlobDataFromUrl(downloadUrl);
    const filename = this.getFilenameWithExtension();
    return { blob, filename };
  }

  async downloadFileVersion(versionId: string): Promise<{ blob: Blob, filename: string }> {
    // PolyHaven doesn't support versions, so this is a no-op
    return Promise.reject(new Error('Versioning not supported for PolyHaven assets'));
  }

  getSource(): string {
    return ExternalAssetSource.Polyhaven;
  }

  getMetadata() {
    return {
      status: 'ready', // PolyHaven assets are always ready
      date: '', // PolyHaven doesn't provide dates in the basic API
      size: 0, // Would need to fetch from download endpoint
      categories: this.asset.categories,
      tags: this.asset.tags,
      providerId: ExternalAssetSource.Polyhaven,
      licence: 'PolyHaven-licence',
      licenceUrl: 'https://polyhaven.com/license',
    };
  }
}
